import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useSnackbar } from 'notistack';
import './List.css';
import '../App.css';
import AppBarComponent from '../components/parts/appBar/AppBarComponent';
import FooterComponent from '../components/FooterComponent';
import LoadingComponent from '../components/LoadingComponent';
import { TableField } from '../components/guestManagePage/TableField';
import { DeleteGuestsModal } from '../components/guestManagePage/DeleteGuestsModal';
import { SearchField } from '../components/guestManagePage/SearchField';
import { Snackbar, Button } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import DeleteIcon from '@material-ui/icons/Delete';
import { useForm, UseFormReturn } from 'react-hook-form';
import { TCloudFileListForm } from '../types/cloudStorage/CloudFileList';
import { TSelectItem } from '../types/common';
import { deleteShareFileGuest } from '../apicalls/cloud/shareFile';
import { deleteSharePictureGuest } from '../apicalls/cloud/sharePicture';
import {
  getSearchedShareFileGuestList,
  getSearchedSharePictureGuestList
} from '../apicalls/cloud/guestManage';
import {
  checkResData,
  createParams,
  getConstructionsSelectBoxData
} from '../utils/common';
import {
  openErrorFlg,
  openSuccessFlg,
  resMessage,
  btnColor
} from '../GlobalState';
import type {
  TGuest,
  TGuestListSortParam
} from '../types/cloudStorage/CloudFileList';

export const GuestManagePage: React.FC = () => {
  // 初期データ
  const defaultValues: TCloudFileListForm = {
    construct_id: { value: 0, label: '--' },
    date_from: '',
    date_end: '',
    keyword: ''
  };

  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [buttonColor] = useRecoilState(btnColor);

  // エラーメッセージ
  const [errorMessage, setErrorMessage] = useRecoilState(resMessage);
  // メッセージ表示フラグ
  const [openSuccess, setOpenSuccess] = useRecoilState(openSuccessFlg);
  // エラーメッセージ表示フラグ
  const [open, setOpen] = useRecoilState(openErrorFlg);
  // ローディング表示フラグ
  const [loading, setLoading] = useState(false);
  // 案件選択値
  const [selectItem, setSelectItem] = useState<TSelectItem[]>([]);
  // 共有先リスト
  const [guestList, setGuestList] = useState<TGuest[]>([]);
  // 選択された共有先リスト
  const [selectedGuestList, setSelectedGuestList] = useState<TGuest[]>([]);
  // 複数の共有先削除モーダル表示フラグ
  const [deleteGuestsModalOpen, setDeleteGuestsModalOpen] =
    useState<boolean>(false);
  // 「資料一覧」が選択されているかの判定フラグ
  const [isFileMode, setIsFileSelected] = useState<boolean>(true);

  const [sortParams, setSortParams] = useState<TGuestListSortParam>({
    sort_field: '',
    sort_direction: ''
  });

  // hook-formの初期設定
  const methods: UseFormReturn<TCloudFileListForm, any> = useForm({
    defaultValues
  });
  const {
    getValues,
    handleSubmit,
    reset,
    formState: { errors }
  } = methods;

  function Alert(props: AlertProps) {
    return <MuiAlert elevation={6} variant='filled' {...props} />;
  }

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  /**
   * 共有先一覧を取得
   */
  const getSearchedGuestList = async () => {
    const formParams = {
      construct_id: getValues('construct_id').value,
      date_from: getValues('date_from'),
      date_end: getValues('date_end'),
      keyword: getValues('keyword'),
      sort_field: sortParams.sort_field,
      sort_direction: sortParams.sort_direction
    };

    const param: string = await createParams(formParams);

    if (isFileMode) {
      // 資料の共有先一覧を取得
      getSearchedShareFileGuestList(param).then((resData: any) => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          setGuestList(data.file_share_guest_list);
        });
      });
    } else {
      // 写真の共有先一覧を取得
      getSearchedSharePictureGuestList(param).then((resData: any) => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          setGuestList(data.picture_share_guest_list);
        });
      });
    }
  };

  /**
   * 「資料一覧」に切り替え
   */
  const handleFileTabClick = () => {
    if (isFileMode) return;
    setIsFileSelected(true);
    reset(); //　検索値リセット
    setSelectedGuestList([]); // 選択された共有先リストをリセット

    getSearchedShareFileGuestList().then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        setGuestList(data.file_share_guest_list);
      });
    });
  };

  /**
   * 「写真一覧」に切り替え
   */
  const handlePictureTabClick = () => {
    if (!isFileMode) return;
    setIsFileSelected(false);
    reset(); //　検索値リセット
    setSelectedGuestList([]); // 選択された共有先リストをリセット

    getSearchedSharePictureGuestList().then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        setGuestList(data.picture_share_guest_list);
      });
    });
  };

  /**
   * 選択された共有先を削除(複数)
   */
  const onDelete = () => {
    if (selectedGuestList.length === 0) return;
    selectedGuestList.forEach(selectedGuest => {
      if (isFileMode) {
        // 資料の共有先を削除
        deleteShareFileGuest(selectedGuest.id).then(resData => {
          checkResData(resData, enqueueSnackbar).then(() => {
            getSearchedGuestList();
            setSelectedGuestList([]);
          });
        });
      } else {
        // 写真の共有先を削除
        deleteSharePictureGuest(selectedGuest.id).then(resData => {
          checkResData(resData, enqueueSnackbar).then(() => {
            getSearchedGuestList();
            setSelectedGuestList([]);
          });
        });
      }
    });
    setDeleteGuestsModalOpen(false);
  };

  /**
   * ソート
   */
  useEffect(() => {
    getSearchedGuestList();
  }, [sortParams]);

  useEffect(() => {
    setLoading(true);
    setErrorMessage('');
    setOpenSuccess(false);
    setOpen(false);

    // 資料の共有状況を全件取得
    getSearchedShareFileGuestList().then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        setGuestList(data.file_share_guest_list);
      });
    });

    // 工事セレクトボックスを取得
    getConstructionsSelectBoxData(setSelectItem, enqueueSnackbar, {
      value: 0,
      label: '--'
    });

    setLoading(false);
  }, []);

  // ----------描画部分----------------------------------
  return (
    <div style={{ backgroundColor: '#F5F5F5' }}>
      {/* ヘッダー */}
      <AppBarComponent patternName='guest_manage' />
      {/* ローディング */}
      {loading ? (
        <>
          <Snackbar open={open} autoHideDuration={4000} onClose={handleClose}>
            <Alert onClose={handleClose} severity='error'>
              {errorMessage}
            </Alert>
          </Snackbar>
          <LoadingComponent />
        </>
      ) : (
        <div
          style={{
            backgroundColor: '#F5F5F5',
            padding: 15,
            minHeight: window.innerHeight - 150
          }}
        >
          {/* ページタイトル */}
          <div style={{ backgroundColor: '#FFFFFF' }}>
            <h1
              style={{
                fontWeight: 'bold',
                margin: 0,
                padding: 15,
                fontSize: 24
              }}
            >
              共有先一覧
            </h1>
          </div>

          <div
            style={{
              backgroundColor: '#FFFFFF',
              padding: 15,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}
          >
            {/* 検索フィールド */}
            <SearchField
              methods={methods}
              selectItem={selectItem}
              handleSubmit={handleSubmit}
              getSearchedGuestList={getSearchedGuestList}
              errors={errors}
            />

            {/* ボタンフィールド */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 10
              }}
            >
              <div style={{ fontWeight: 'bold', marginRight: 16 }}>
                選択した共有先を
              </div>
              <Button
                style={{
                  backgroundColor: buttonColor,
                  color: '#fff',
                  marginRight: 10
                }}
                variant='contained'
                color='secondary'
                onClick={() => setDeleteGuestsModalOpen(true)}
                disabled={selectedGuestList.length === 0}
              >
                <DeleteIcon />
                削除
              </Button>
            </div>
          </div>

          {/* タブフィールド */}
          <div
            style={{
              backgroundColor: '#FFFFFF',
              paddingLeft: 20,
              display: 'flex'
            }}
          >
            <div
              className={isFileMode ? classes.activeTab : classes.tab}
              onClick={handleFileTabClick}
            >
              資料一覧
            </div>
            <div
              className={isFileMode ? classes.tab : classes.activeTab}
              onClick={handlePictureTabClick}
            >
              写真一覧
            </div>
          </div>

          {/* テーブルフィールド */}
          <TableField
            guestList={guestList}
            selectedGuestList={selectedGuestList}
            setSelectedGuestList={setSelectedGuestList}
            getSearchedGuestList={getSearchedGuestList}
            isFileMode={isFileMode}
            sortParams={sortParams}
            setSortParams={setSortParams}
          />

          {/* 複数の共有先削除モーダル */}
          <DeleteGuestsModal
            selectedGuestList={selectedGuestList}
            deleteGuestsModalOpen={deleteGuestsModalOpen}
            setDeleteGuestsModalOpen={setDeleteGuestsModalOpen}
            onDelete={onDelete}
          />
        </div>
      )}
      {/* フッター */}
      <FooterComponent />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    activeTab: {
      color: '#000000',
      fontSize: 14,
      border: '1px solid #cccccc',
      padding: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#FFFFFF',
      minWidth: 80,
      cursor: 'pointer'
    },
    tab: {
      color: '#FFFFFF',
      fontSize: 14,
      border: '1px solid #cccccc',
      padding: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#0099cc',
      minWidth: 80,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#52555f'
      }
    }
  })
);
